import React from 'react';
import { Box } from '@mui/material';
import { tokens } from '../../../theme/tokens';
import { taxonomyLabel } from '../mappingModel';

interface CategorySelectProps {
  'aria-label': string;
  /** A taxonomy GID; empty means the empty option. */
  value: string;
  options: Array<{ gid: string; label: string }>;
  onChange: (gid: string) => void;
  /** Text of the empty option ("Keep category", "Keep as is"). Omit for a select that always holds a category. */
  emptyLabel?: string;
  sx?: Record<string, unknown>;
}

/** A plain select over the Shopify standard categories, styled as an input. */
const CategorySelect: React.FC<CategorySelectProps> = ({ value, options, onChange, emptyLabel, sx, ...rest }) => {
  const known = options.some((option) => option.gid === value);
  return (
    <Box
      component="select"
      aria-label={rest['aria-label']}
      value={value}
      onChange={(event: React.ChangeEvent<HTMLSelectElement>) => onChange(event.target.value)}
      sx={{
        width: '100%',
        minWidth: 0,
        height: 36,
        px: 1,
        border: '1px solid',
        borderColor: tokens.line.default,
        borderRadius: '8px',
        bgcolor: tokens.surface.card,
        color: value ? tokens.ink.primary : tokens.ink.faint,
        font: 'inherit',
        fontSize: 13,
        outline: 'none',
        '&:focus': { borderColor: tokens.accent.main },
        ...sx,
      }}
    >
      {emptyLabel !== undefined && <option value="">{emptyLabel}</option>}
      {value && !known && <option value={value}>{taxonomyLabel(options, value)}</option>}
      {options.map((option) => (
        <option key={option.gid} value={option.gid}>{option.label}</option>
      ))}
    </Box>
  );
};

export default CategorySelect;
